import styled from 'styled-components'
import { orange, blue, black, textMuted, mutedGrey, lightGrey, surface2, border1 } from '@/theme/colors'
import { radii, motion } from '@/theme/tokens'
import { fontSizes, leading } from '@/theme/typography'

export const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
`

export const Title = styled.h3`
	margin: 0;
	font-size: ${fontSizes.title};
	font-weight: 700;
	color: ${black};
	line-height: ${leading.heading};
	word-break: break-word;
`

export const FieldRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

export const FieldHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`

export const FieldLabel = styled.label`
	font-size: ${fontSizes.caption};
	font-weight: 500;
	color: ${textMuted};
	display: inline-flex;
	align-items: center;
	gap: 6px;

	svg {
		width: 14px;
		height: 14px;
		fill: currentColor;
	}
`

export const FieldValue = styled.div`
	font-size: ${fontSizes.small};
	color: ${black};
	line-height: ${leading.body};
	white-space: pre-wrap;
	word-break: break-word;
	padding: 8px 8px;
	margin: 0 -8px;
	border-radius: ${radii.small};
	background: transparent;
`

export const EmptyValue = styled(FieldValue)`
	color: ${mutedGrey};
	font-style: italic;
`

export const LinkListReadOnly = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin: 0 -8px;
`

export const LinkUrl = styled.span`
	font-size: ${fontSizes.caption};
	color: ${blue};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`

export const LinkArrow = styled.span`
	display: inline-flex;
	align-items: center;
	flex-shrink: 0;
	color: ${textMuted};
	transition: color ${motion.fast};
`

/**
 * One link as a single compact line: a small square thumbnail, the platform's
 * name (or the address when it came from no platform), then an outward arrow
 * at the end. No border; the row only tints on hover so the list reads as
 * text rather than a stack of cards.
 */
export const LinkPreview = styled.a`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 6px 8px;
	border-radius: ${radii.small};
	background: transparent;
	text-decoration: none;
	transition: background-color ${motion.fast};
	overflow: hidden;
	min-width: 0;

	&:hover {
		background: ${surface2};
	}

	&:hover ${LinkUrl} {
		text-decoration: underline;
	}

	&:hover ${LinkArrow} {
		color: ${black};
	}
`

export const StaticLinkPreview = styled(LinkPreview)`
	cursor: default;

	&:hover {
		background: transparent;
	}
`

export const LinkThumbnail = styled.div<{ $src?: string }>`
	width: 36px;
	height: 36px;
	min-width: 36px;
	border-radius: ${radii.tiny};
	border: 1px solid ${border1};
	background: ${props => (props.$src ? `url(${props.$src})` : lightGrey)};
	background-size: cover;
	background-position: center;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	flex-shrink: 0;
	box-sizing: border-box;
`

export const LinkInfo = styled.div`
	flex: 1;
	min-width: 0;
	display: flex;
	align-items: baseline;
	gap: 6px;
	overflow: hidden;
`

export const LinkSource = styled.span<{ $color?: string }>`
	font-size: ${fontSizes.caption};
	font-weight: 700;
	color: ${({ $color }) => $color ?? textMuted};
	flex-shrink: 0;
`

export const LocationDisplay = styled.a`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	color: ${orange};
	text-decoration: none;
	font-size: ${fontSizes.small};
	padding: 8px 8px;
	margin: 0 -8px;
	width: fit-content;

	&:hover {
		text-decoration: underline;
	}
`

export const LocationSecondary = styled.div`
	font-size: ${fontSizes.micro};
	color: ${textMuted};
	margin-top: 4px;
`
